import { createContext, useContext, useEffect, useState, ReactNode, useCallback, useRef } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

export type AppRole = 'admin' | 'staff' | 'super_admin';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  role: AppRole | null;
  establishmentId: string | null;
  loading: boolean;
  signOut: () => Promise<void>;
  isSuperAdmin: boolean;
  isAdmin: boolean;
  isStaff: boolean;
  // Super admin establishment override
  overrideEstablishmentId: string | null;
  setOverrideEstablishmentId: (id: string | null) => void;
  // Dashboard last seen
  dashboardLastSeenAt: Date | null;
  updateDashboardLastSeen: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const OVERRIDE_STORAGE_KEY = 'superAdminOverrideEstablishment';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const currentUserIdRef = useRef<string | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [role, setRole] = useState<AppRole | null>(null);
  const [baseEstablishmentId, setBaseEstablishmentId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [dashboardLastSeenAt, setDashboardLastSeenAt] = useState<Date | null>(null);
  
  // Super admin override - persisted in localStorage
  const [overrideEstablishmentId, setOverrideEstablishmentIdState] = useState<string | null>(() => {
    try {
      return localStorage.getItem(OVERRIDE_STORAGE_KEY);
    } catch {
      return null;
    }
  });

  // Setter that also persists to localStorage
  const setOverrideEstablishmentId = useCallback((id: string | null) => {
    setOverrideEstablishmentIdState(id);
    try {
      if (id) {
        localStorage.setItem(OVERRIDE_STORAGE_KEY, id);
      } else {
        localStorage.removeItem(OVERRIDE_STORAGE_KEY);
      }
    } catch (e) {
      console.error('[AuthContext] Error persisting override:', e);
    }
  }, []);

  // Fonction pour forcer la déconnexion et rediriger
  const forceSignOut = useCallback(async (reason: string) => {
    console.log(`[AuthContext] Force sign out: ${reason}`);
    try {
      await supabase.auth.signOut();
    } catch (e) {
      console.error('[AuthContext] Error during force sign out:', e);
    }
    setUser(null);
    setSession(null);
    setRole(null);
    setBaseEstablishmentId(null);
    setOverrideEstablishmentId(null);
    
    // Rediriger vers login si on n'y est pas déjà
    if (!window.location.pathname.includes('/login') && 
        !window.location.pathname.includes('/set-password') &&
        !window.location.pathname.includes('/verify-email') &&
        !window.location.pathname.includes('/signup') &&
        window.location.pathname !== '/') {
      window.location.href = '/login';
    }
  }, [setOverrideEstablishmentId]);

  // Fetch user role and establishment
  const fetchUserRoleAndEstablishment = useCallback(async (userId: string): Promise<void> => {
    try {
      // First check if user is super admin
      const { data: superAdminData } = await supabase
        .from('super_admins' as any)
        .select('id')
        .eq('user_id', userId)
        .maybeSingle();

      // Always fetch user_roles to get establishment_id (even for super admins)
      const { data: roleData, error: roleError } = await supabase
        .from('user_roles')
        .select('role, establishment_id, dashboard_last_seen_at')
        .eq('user_id', userId)
        .maybeSingle() as { data: { role: string; establishment_id: string; dashboard_last_seen_at: string | null } | null; error: any };

      if (roleError && roleError.code !== 'PGRST116') {
        console.error('[AuthContext] Error fetching role:', roleError);
      }

      if (superAdminData) {
        setRole('super_admin');
        // Super admin keeps their establishment_id if they have one in user_roles
        setBaseEstablishmentId(roleData?.establishment_id ?? null);
      } else {
        setRole((roleData?.role as AppRole) ?? null);
        setBaseEstablishmentId(roleData?.establishment_id ?? null);
        // Clear override for non-super-admins
        setOverrideEstablishmentId(null);
      }

      // Read dashboard_last_seen_at
      if (roleData?.dashboard_last_seen_at) {
        setDashboardLastSeenAt(new Date(roleData.dashboard_last_seen_at));
      } else {
        setDashboardLastSeenAt(null);
      }
    } catch (e) {
      console.error('[AuthContext] Error in fetchUserRoleAndEstablishment:', e);
      setRole(null);
      setBaseEstablishmentId(null);
    } finally {
      // Always set loading to false after role fetch completes
      setLoading(false);
    }
  }, [setOverrideEstablishmentId]);

  // Vérifier périodiquement si la session est toujours valide
  useEffect(() => {
    const checkSessionValidity = async () => {
      // Ne vérifier que si on a une session
      if (!session) return;

      try {
        // Utiliser getUser() au lieu de getSession() : envoie une requête réseau
        // qui rafraîchit automatiquement le token si expiré (crucial après veille iPad)
        const { data: authData, error: authError } = await supabase.auth.getUser();
        
        if (authError) {
          // Distinguer erreurs d'auth réelles vs erreurs réseau transitoires
          const errorMessage = authError.message?.toLowerCase() || '';
          const isAuthError = 
            errorMessage.includes('session_not_found') ||
            errorMessage.includes('invalid') ||
            errorMessage.includes('expired') ||
            errorMessage.includes('not authenticated') ||
            (authError as any).status === 401 ||
            (authError as any).status === 403;
          
          if (isAuthError) {
            console.log('[AuthContext] Auth error - signing out:', authError.message);
            await forceSignOut('Session invalid');
            return;
          }
          
          // Erreur réseau transitoire (iPad qui se réveille, WiFi instable) → ignorer
          console.warn('[AuthContext] Transient error during session check, skipping:', authError.message);
          return;
        }

        if (!authData?.user) {
          console.log('[AuthContext] No user returned - signing out');
          await forceSignOut('Session expired');
          return;
        }

        // Vérifier si l'utilisateur existe toujours dans user_roles ou super_admins
        const userId = authData.user.id;

        // Check super_admins first
        const { data: superAdminData } = await supabase
          .from('super_admins' as any)
          .select('id')
          .eq('user_id', userId)
          .maybeSingle();

        // Super admin is always valid
        if (superAdminData) {
          return;
        }

        // Always fetch user_roles to verify user still exists
        const { data: roleData, error: roleError } = await supabase
          .from('user_roles')
          .select('role, establishment_id')
          .eq('user_id', userId)
          .maybeSingle();

        // Erreurs réseau transitoires → ne PAS déconnecter
        if (roleError && roleError.code !== 'PGRST116') {
          console.warn('[AuthContext] Role check transient error, skipping sign out:', roleError.code);
          return;
        }

        // Si l'utilisateur avait un rôle mais que la ligne n'existe vraiment plus
        if (role && !roleData) {
          console.log('[AuthContext] User role no longer exists - user may have been deleted');
          await forceSignOut('User deleted');
          return;
        }
      } catch (e) {
        // Catch-all pour erreurs réseau (fetch failed, timeout, etc.) → ne PAS déconnecter
        console.warn('[AuthContext] Session check network exception, skipping:', e);
      }
    };

    // Vérifier la session toutes les 30 secondes
    // Note: On n'écoute PAS visibilitychange intentionnellement — sur iPad/mobile,
    // le réseau peut prendre quelques secondes à se rétablir après un changement d'app,
    // ce qui provoquerait des déconnexions intempestives sur des erreurs réseau transitoires.
    // Le refresh automatique de Supabase (autoRefreshToken) gère la session nativement.
    const interval = setInterval(checkSessionValidity, 30000);

    return () => {
      clearInterval(interval);
    };
  }, [session, role, forceSignOut]);

  useEffect(() => {
    // Get initial session
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setUser(session?.user ?? null);
      
      if (session?.user && currentUserIdRef.current === session.user.id) {
        // Already handled by onAuthStateChange
        return;
      }
      currentUserIdRef.current = session?.user?.id ?? null;
      if (session?.user) {
        // Loading will be set to false inside fetchUserRoleAndEstablishment
        fetchUserRoleAndEstablishment(session.user.id);
      } else {
        setLoading(false);
      }
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        console.log('[AuthContext] Auth state changed:', event);
        
        // Gérer les événements de déconnexion
        if (event === 'SIGNED_OUT' || (event === 'TOKEN_REFRESHED' && !session)) {
          currentUserIdRef.current = null;
          setSession(null);
          setUser(null);
          setRole(null);
          setBaseEstablishmentId(null);
          setOverrideEstablishmentId(null);
          setLoading(false);
          return;
        }

        // Only synchronous state updates to prevent deadlock
        setSession(session);
        setUser(session?.user ?? null);

        // Same user re-authenticated (password check, password change, token refresh):
        // keep role/establishment as-is, no loading screen.
        const sameUser = !!session?.user && session.user.id === currentUserIdRef.current;
        currentUserIdRef.current = session?.user?.id ?? null;
        if (sameUser && event !== 'INITIAL_SESSION') {
          return;
        }
        
        // Keep loading true until role fetch completes
        if (session?.user) {
          setLoading(true);
          // Defer Supabase call with setTimeout to prevent deadlock
          // Loading will be set to false inside fetchUserRoleAndEstablishment
          setTimeout(() => {
            fetchUserRoleAndEstablishment(session.user.id);
          }, 0);
        } else {
          setRole(null);
          setBaseEstablishmentId(null);
          setLoading(false);
        }
      }
    );

    return () => subscription.unsubscribe();
  }, [fetchUserRoleAndEstablishment, setOverrideEstablishmentId]);

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setRole(null);
    setBaseEstablishmentId(null);
    setOverrideEstablishmentId(null);
  };

  const isSuperAdmin = role === 'super_admin';
  const isAdmin = role === 'admin' || role === 'super_admin';
  const isStaff = role === 'staff' || role === 'admin' || role === 'super_admin';

  // Effective establishment ID: use override if super admin, otherwise base
  const establishmentId = isSuperAdmin && overrideEstablishmentId 
    ? overrideEstablishmentId 
    : baseEstablishmentId;

  // Update dashboard last seen timestamp
  const updateDashboardLastSeen = useCallback(async () => {
    if (!user || !establishmentId) return;
    try {
      const now = new Date();
      await supabase
        .from('user_roles')
        .update({ dashboard_last_seen_at: now.toISOString() } as any)
        .eq('user_id', user.id)
        .eq('establishment_id', establishmentId);
      setDashboardLastSeenAt(now);
    } catch (e) {
      console.error('[AuthContext] Error updating dashboard_last_seen_at:', e);
    }
  }, [user, establishmentId]);

  return (
    <AuthContext.Provider value={{ 
      user, 
      session, 
      role, 
      establishmentId,
      loading, 
      signOut,
      isSuperAdmin,
      isAdmin,
      isStaff,
      overrideEstablishmentId,
      setOverrideEstablishmentId,
      dashboardLastSeenAt,
      updateDashboardLastSeen,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
};
